import {AsyncTypeahead} from "react-bootstrap-typeahead";
import {PagesHelper} from "../../../common/PagesHelper";
import {CLinkOwnerOption, CLinkRecord} from "../../../utils/types";
import parse from 'html-react-parser';
import TagsList from "../../clinks/tags/TagsList";
import {formatSearchInRowToBold} from "../../../utils/misc";
import {InputGroup} from "react-bootstrap";
import AutocompleteIncludeExclude from "../../library/AutocompleteIncludeExclude";
import { useState, useEffect, useRef } from "react";

function CLinkOwnersAutocomplete(props: any) {
  const [searchText, setSearchText] = useState("");
  const typeaheadRef = useRef<any>(null);
  const { reactRef } = props;

  const labelKeyFunction = function (cLinkOwner: CLinkOwnerOption) {
    return PagesHelper.fromCLinkOwnerOptionToString(cLinkOwner);
  }

  const handleKeepOpenClick = (option: CLinkOwnerOption) => {
    const currentSelected = props.selected || [];
    const optionString = PagesHelper.fromCLinkOwnerOptionToString(option);
    const isSelected = currentSelected.some((selected: CLinkOwnerOption) =>
      PagesHelper.fromCLinkOwnerOptionToString(selected) === optionString
    );

    let newSelection;
    if (isSelected) {
      // Remove from selection
      newSelection = currentSelected.filter((selected: CLinkOwnerOption) =>
        PagesHelper.fromCLinkOwnerOptionToString(selected) !== optionString
      );
    } else {
      // Add to selection
      newSelection = [...currentSelected, option];
    }

    props.onChange(newSelection);

    // Restore search text and trigger search again to keep the dropdown showing clinks
    setTimeout(() => {
      if (typeaheadRef.current && searchText) {
        typeaheadRef.current.setState({ text: searchText });
        // Trigger search again with the same text
        if (props.searchCLinkOwners) {
          props.searchCLinkOwners(searchText);
        }
      }
    }, 0);
  };

  const handleRegularOnChange = (selected: CLinkOwnerOption[]) => {
    // Detect if handleKeepOpenClick already handled this (duplicate in selection)
    const selectedStrings = selected.map(s => PagesHelper.fromCLinkOwnerOptionToString(s));
    const hasDuplicate = selectedStrings.length !== new Set(selectedStrings).size;

    if (hasDuplicate || (selected.length > 0 && selected[selected.length - 1] &&
      props.selected?.some((s: CLinkOwnerOption) =>
        PagesHelper.fromCLinkOwnerOptionToString(s) === PagesHelper.fromCLinkOwnerOptionToString(selected[selected.length - 1])
      ))) {
      return; // handleKeepOpenClick already handled it
    }

    // Pass through X button removals and clear button
    props.onChange(selected);
  };

  const handleInputChange = (text: string) => {
    setSearchText(text);
    if (props.onInputChange) {
      props.onInputChange(text);
    }
  };

  // Combine refs
  useEffect(() => {
    if (reactRef) {
      if (typeof reactRef === 'function') {
        reactRef(typeaheadRef.current);
      } else {
        reactRef.current = typeaheadRef.current;
      }
    }
  }, [reactRef]);

  const trafficSourceResponseDto = props.trafficSourceResponseDto;
  const filterBy = (option: CLinkOwnerOption, filterProps: any) => {
    // Filter out already selected items
    const optionString = PagesHelper.fromCLinkOwnerOptionToString(option);
    const isAlreadySelected = filterProps.selected?.some((selected: CLinkOwnerOption) =>
      PagesHelper.fromCLinkOwnerOptionToString(selected) === optionString
    );
    return !isAlreadySelected;
  };

  const renderMenuItemChildren = (option: CLinkOwnerOption, menuProps: any, index: any) => {
    const cLinkRecord: CLinkRecord = (option.record as CLinkRecord);
    const ownerOrTagRecord: string = (option.record as string);
    const publisher = trafficSourceResponseDto && trafficSourceResponseDto.publishersArray ? trafficSourceResponseDto.publishersArray.find((p: any) => p.id === cLinkRecord.apptapPublisherId) : null;
    const apptapPublisherName = publisher?.record?.apptapPublisherName ? publisher?.record?.apptapPublisherName : "";

    const search = menuProps.text;
    let menuItem = null;

    if (cLinkRecord.cLinkId) {
      const wordsToSearch = search.split(" ");
      let firstRow: string = cLinkRecord.cLinkId;
      let secondRow: string = `${apptapPublisherName} (${cLinkRecord.apptapPublisherId}:${cLinkRecord.apptapSiteId}`;
      let thirdRow: string = cLinkRecord.productId;
      wordsToSearch.forEach((word: string) => {
        if (word) {
          firstRow = formatSearchInRowToBold(firstRow, word);
          secondRow = formatSearchInRowToBold(secondRow, word);
          thirdRow = formatSearchInRowToBold(thirdRow, word);
        }
      });

      menuItem = (
        <div style={{paddingLeft: "25px"}}>
          <div>{parse(firstRow)}</div>
          <div>{parse(secondRow)}</div>
          <div>{parse(thirdRow)} <TagsList tagsList={cLinkRecord.tags} search={search}/></div>
        </div>
      )
    } else if (ownerOrTagRecord.includes("#")) {
      menuItem = (
        <div style={{fontWeight: "bold"}}>
          <TagsList tagsList={[option.text]} search={search} containerStyles={{margin: "0 4px 0 0"}}
                    tagStyles={{margin: "0 4px"}}/>
        </div>
      )
    } else {
      menuItem = (
        <div style={{fontWeight: "bold"}}>
          <strong>{ownerOrTagRecord}</strong>
        </div>
      )
    }

    return (
      <div
        style={{ cursor: "pointer", padding: "0.25rem 1rem" }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleKeepOpenClick(option);
        }}
      >
        {menuItem}
      </div>
    );
  }

  return (
    <InputGroup>
      {
        props.useIncludeExclude ?
          <AutocompleteIncludeExclude
            defaultIncludeExclude={props.defaultIncludeExclude}
            includeExcludeOnChange={props.includeExcludeOnChange}
          />
          :
          null
      }
      <AsyncTypeahead size="sm"
        id="cLinkOwnersAutocomplete"
        labelKey={labelKeyFunction}
        renderMenuItemChildren={renderMenuItemChildren}
        onSearch={props.searchCLinkOwners}
        isLoading={props.clinkOwnersIsLoading}
        onInputChange={handleInputChange}
        options={props.options}
        selected={props.selected}
        onChange={handleRegularOnChange}
        disabled={props.disabled}
        ref={typeaheadRef}
        useCache={false}
        placeholder={props.placeholder}
        minLength={0}
        filterBy={filterBy}
        clearButton
        multiple
      />
    </InputGroup>
  );
}

export default CLinkOwnersAutocomplete;